import {
  IDENTIFICATION_SOURCE_ID_SEPARATOR,
  IdentificationSourceIdPrefix,
  PHONE_SOURCE_ID_PREFIXES_FOR_UTM,
} from '@reach/shared-types/identification-source-id';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, ExternalLink, Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PartnerResourceModal } from '../../components/PartnerResourceModal';
import { Button } from '../../components/ui/button';
import type { BillableEvent, Communication, Identification, PartnerUser, Visit } from '../../lib/api';
import { partnerApi } from '../../lib/api';

// Types for user details and timeline
type UserDetails = PartnerUser & {
  subscriptionStatus?: string | null;
  billableEventsCount?: number;
  totalRevenue?: number;
  resourceData?: Record<string, unknown>;
};

// Types for reference navigation
interface BreadcrumbItem {
  fieldName: string;
  resourceId: string;
  schemaId: string;
  displayName: string;
}

interface ReferenceModalState {
  isOpen: boolean;
  resourceId: string | null;
  schemaId: string | null;
  fieldName: string | null;
  breadcrumbs: BreadcrumbItem[];
}

interface TimelineEvent {
  date: Date;
  type:
    | 'user_created'
    | 'user_synced'
    | 'visit'
    | 'identification'
    | 'phone_call'
    | 'billable_event'
    | 'communication_email'
    | 'communication_sms';
  title: string;
  description: string;
  data: UserDetails | Visit | Identification | BillableEvent | Communication;
}

// Reference Resource Modal Component
function ReferenceResourceModal({
  modalState,
  onClose,
  tenantId,
}: {
  modalState: ReferenceModalState;
  onClose: () => void;
  onNavigateToReference: (
    resourceId: string,
    schemaId: string,
    fieldName: string,
    displayName: string,
  ) => void;
  tenantId: string;
}) {
  // Error boundary state
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Reset error state when modal opens
  useEffect(() => {
    if (modalState.isOpen) {
      setHasError(false);
      setErrorMessage('');
    }
  }, [modalState.isOpen]);
  const { data: schemaDefinition } = useQuery({
    queryKey: ['referenced-schema', modalState.schemaId],
    queryFn: () => {
      if (!modalState.schemaId) {
        throw new Error('Schema ID is required');
      }
      return partnerApi.getSchemaDefinition(modalState.schemaId);
    },
    enabled: modalState.isOpen && !!modalState.schemaId,
  });

  // Get the referenced schema name from the $ref
  const getReferencedSchemaName = (
    schemaDef: Record<string, unknown>,
    fieldName: string,
  ): string | null => {
    const properties = (schemaDef.properties as Record<string, unknown>) || {};
    const fieldDef = properties[fieldName] as Record<string, unknown>;

    if (fieldDef?.$ref && typeof fieldDef.$ref === 'string') {
      // Extract schema name from $ref like "reach:schemas/SchemaName"
      const match = fieldDef.$ref.match(/reach:schemas\/(.+)/);
      return match ? match[1] : null;
    }

    return null;
  };

  const referencedSchemaName =
    schemaDefinition && modalState.fieldName && schemaDefinition.schema
      ? getReferencedSchemaName(schemaDefinition.schema, modalState.fieldName)
      : null;

  const {
    data: resource,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['referenced-resource', modalState.resourceId, referencedSchemaName],
    queryFn: async () => {
      try {
        // Use the referenced schema name if available, otherwise fall back to the provided schemaId
        const schemaToUse = referencedSchemaName || modalState.schemaId;

        return await partnerApi.getPartnerResourceByExternalId(
          tenantId,
          modalState.resourceId!,
          schemaToUse!,
        );
      } catch (err) {
        console.error('Error fetching referenced resource:', err);
        setHasError(true);
        setErrorMessage(err instanceof Error ? err.message : 'Failed to load resource');
        throw err;
      }
    },
    enabled: modalState.isOpen && !!modalState.resourceId && !!referencedSchemaName,
    retry: false, // Don't retry on error to prevent infinite loops
  });

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'string' && value.length === 0) return 'Empty';
    return String(value);
  };

  const formatFieldName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  if (!modalState.isOpen) return null;

  // Wrap the entire modal in error boundary
  try {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Dialog */}
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">Referenced Resource</h2>
                {/* Breadcrumb */}
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  {modalState.breadcrumbs?.map((crumb, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <ChevronRight className="w-4 h-4 mx-2" />}
                      <span className="text-purple-600 font-medium">{crumb.displayName}</span>
                    </React.Fragment>
                  ))}
                  {modalState.fieldName && (
                    <>
                      <ChevronRight className="w-4 h-4 mx-2" />
                      <span className="text-gray-900 font-medium">
                        {formatFieldName(modalState.fieldName)} ({modalState.resourceId})
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {hasError ? (
                <div className="bg-red-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                  <h3 className="text-sm font-medium text-red-800">Error loading resource</h3>
                  <div className="mt-2 text-sm text-red-700">{errorMessage}</div>
                  <div className="mt-4">
                    <button
                      onClick={onClose}
                      className="text-sm text-red-600 hover:text-red-700 underline">
                      Close Modal
                    </button>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-gray-500">Loading resource details...</span>
                </div>
              ) : error ? (
                <div className="bg-red-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                  <h3 className="text-sm font-medium text-red-800">Error loading resource</h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error instanceof Error ? error.message : 'Resource not found'}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={onClose}
                      className="text-sm text-red-600 hover:text-red-700 underline">
                      Close Modal
                    </button>
                  </div>
                </div>
              ) : resource ? (
                <div className="space-y-6">
                  {/* Resource Information */}
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(resource)
                        .filter(([key]) => key !== 'data') // Exclude the data object
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, value]) => (
                          <div key={key} className="break-words">
                            <div className="text-xs text-gray-500 font-medium">
                              {formatFieldName(key)}
                            </div>
                            <div className="text-sm mt-1 text-gray-900">
                              {typeof value === 'object' && value !== null ? (
                                <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-x-auto max-h-32">
                                  {formatValue(value)}
                                </pre>
                              ) : (
                                formatValue(value)
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <h3 className="text-sm font-semibold text-gray-900">No Resource Found</h3>
                  <p className="mt-1 text-sm text-gray-500">No resource was found for this reference.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering ReferenceResourceModal:', error);
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-red-800 mb-4">Modal Error</h3>
              <p className="text-sm text-red-700 mb-4">
                An error occurred while rendering the modal. Please try again.
              </p>
              <button
                onClick={onClose}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

// Component to display resource relationships
function ResourceRelationships({
  externalId,
  tenantId,
  onResourceClick,
}: {
  externalId: string;
  tenantId: string;
  onResourceClick: (resourceId: string, resourceType: string, displayName?: string) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-resource-relationships', externalId, tenantId],
    queryFn: () => partnerApi.getTenantUserResourceRelationships(externalId, tenantId),
    enabled: !!tenantId,
  });

  if (isLoading) {
    return (
      <div className="border-t border-gray-200 pt-6 mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Resource Relationships</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border-t border-gray-200 pt-6 mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Resource Relationships</h3>
        <div className="text-sm text-gray-500 text-center py-4">Failed to load relationships</div>
      </div>
    );
  }

  const relationships = data.data;
  const totalInbound = relationships.inboundReferences.reduce((sum, ref) => sum + ref.count, 0);
  const totalOutbound = relationships.outboundReferences.length;

  if (totalInbound === 0 && totalOutbound === 0) {
    return (
      <div className="border-t border-gray-200 pt-6 mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Resource Relationships</h3>
        <div className="text-sm text-gray-500 italic">No resource relationships found</div>
      </div>
    );
  }

  const formatFieldName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  const handleResourceIdClick = async (resourceId: string, category: string) => {
    // Show the resource modal for any schema type
    onResourceClick(resourceId, category);
  };

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Resource Relationships</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Inbound References */}
        <div>
          <div className="flex items-center mb-3">
            <h4 className="text-sm font-medium text-gray-900">Referenced By ({totalInbound})</h4>
          </div>
          {relationships.inboundReferences.length === 0 ? (
            <div className="text-sm text-gray-500 italic">No resources reference this user</div>
          ) : (
            <div className="space-y-2">
              {relationships.inboundReferences.map(ref => (
                <div
                  key={`${ref.schemaId}-${ref.referenceField}`}
                  className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{ref.schemaName}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        via {ref.schemaName}.{formatFieldName(ref.referenceField)}
                      </div>
                      {ref.resourceIds && ref.resourceIds.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-600 font-medium mb-1">Resource IDs:</div>
                          <div className="flex flex-wrap gap-1">
                            {ref.resourceIds.map((resourceId, index) => {
                              const isClickable = true;
                              return (
                                <span
                                  key={index}
                                  onClick={() => handleResourceIdClick(resourceId, ref.category)}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${
                                    isClickable
                                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                  {resourceId}
                                  {isClickable && <ExternalLink className="w-3 h-3 ml-1" />}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-lg font-semibold text-blue-600 ml-4">{ref.count}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Outbound References */}
        <div>
          <div className="flex items-center mb-3">
            <h4 className="text-sm font-medium text-gray-900">References ({totalOutbound})</h4>
          </div>
          {relationships.outboundReferences.length === 0 ? (
            <div className="text-sm text-gray-500 italic">This user doesn't reference any resources</div>
          ) : (
            <div className="space-y-2">
              {relationships.outboundReferences.map(ref => (
                <div
                  key={`${ref.schemaId}-${ref.referenceField}`}
                  className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{ref.schemaName}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        via {relationships.primaryUserSchemaName}.{formatFieldName(ref.referenceField)}
                      </div>
                      {ref.resourceIds && ref.resourceIds.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-600 font-medium mb-1">Resource IDs:</div>
                          <div className="flex flex-wrap gap-1">
                            {ref.resourceIds.map((resourceId, index) => {
                              const isClickable = true;
                              return (
                                <span
                                  key={index}
                                  onClick={() => handleResourceIdClick(resourceId, ref.category)}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono ${
                                    isClickable
                                      ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 cursor-pointer transition-colors'
                                      : 'bg-purple-100 text-purple-800'
                                  }`}>
                                  {resourceId}
                                  {isClickable && <ExternalLink className="w-3 h-3 ml-1" />}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-lg font-semibold text-purple-600 ml-4">{ref.count}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Component to display custom schema fields
function CustomSchemaFields({ data, tenantId }: { data: Record<string, unknown>; tenantId: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxCollapsedHeight = 200; // pixels
  const [modalState, setModalState] = useState<ReferenceModalState>({
    isOpen: false,
    resourceId: null,
    schemaId: null,
    fieldName: null,
    breadcrumbs: [],
  });

  // Fetch the primary contact schema definition to identify reference fields
  const { data: schemaData } = useQuery({
    queryKey: ['primary-contact-schema-id'],
    queryFn: () => partnerApi.getPrimaryContactSchemaId(),
  });

  // Fetch the schema definition if we have the schema ID
  const { data: schemaDefinition } = useQuery({
    queryKey: ['schema-definition', schemaData?.primaryUserSchemaId],
    queryFn: () => partnerApi.getSchemaDefinition(schemaData!.primaryUserSchemaId!),
    enabled: !!schemaData?.primaryUserSchemaId,
  });

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'string' && value.length === 0) return 'Empty';
    return String(value);
  };

  const formatFieldName = (key: string): string => {
    // Convert snake_case or camelCase to Title Case
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  // Extract schema properties and identify reference fields
  const getReferenceFields = (schema: Record<string, unknown>): Set<string> => {
    const referenceFields = new Set<string>();
    const properties = (schema.properties as Record<string, unknown>) || {};

    Object.entries(properties).forEach(([fieldName, prop]) => {
      const propObj = prop as Record<string, unknown>;
      if (propObj.$ref && typeof propObj.$ref === 'string') {
        referenceFields.add(fieldName);
      }
    });

    return referenceFields;
  };

  const referenceFields = schemaDefinition
    ? getReferenceFields(schemaDefinition.schema)
    : new Set<string>();

  const handleNavigateToReference = (resourceId: string, schemaId: string, fieldName: string) => {
    setModalState({
      isOpen: true,
      resourceId,
      schemaId,
      fieldName,
      breadcrumbs: [
        ...modalState.breadcrumbs,
        {
          fieldName: modalState.fieldName || '',
          resourceId: modalState.resourceId || '',
          schemaId: modalState.schemaId || '',
          displayName: modalState.fieldName ? formatFieldName(modalState.fieldName) : '',
        },
      ],
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      resourceId: null,
      schemaId: null,
      fieldName: null,
      breadcrumbs: [],
    });
  };

  const fields = Object.entries(data)
    .filter(([key]) => key !== 'id' && key !== 'external_id') // Skip internal fields
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Custom Fields ({fields.length})</h3>
        {fields.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            {isExpanded ? 'Collapse' : 'View All'}
          </button>
        )}
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h4 className="text-sm font-medium text-gray-900">No Custom Fields</h4>
          <p className="text-sm text-gray-500 mt-1">This user has no custom schema fields to display.</p>
        </div>
      ) : (
        <div
          className={`overflow-hidden transition-all duration-300 ${isExpanded ? '' : 'relative'}`}
          style={!isExpanded ? { maxHeight: `${maxCollapsedHeight}px` } : undefined}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fields.map(([key, value]) => {
              const isReferenceField = referenceFields.has(key);
              return (
                <div key={key} className="break-words">
                  <div className="text-xs text-gray-500 font-medium">
                    {formatFieldName(key)}
                    {isReferenceField && <span className="ml-1 font-bold">($ref)</span>}
                  </div>
                  <div className="text-sm mt-1 text-gray-900">
                    {typeof value === 'object' && value !== null ? (
                      <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-x-auto">
                        {formatValue(value)}
                      </pre>
                    ) : isReferenceField ? (
                      <button
                        onClick={() => {
                          setModalState({
                            isOpen: true,
                            resourceId: String(value),
                            schemaId: schemaData?.primaryUserSchemaId || '',
                            fieldName: key,
                            breadcrumbs: [],
                          });
                        }}
                        className="text-purple-600 hover:text-purple-700 font-medium underline cursor-pointer">
                        {formatValue(value)}
                      </button>
                    ) : (
                      formatValue(value)
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gradient overlay when collapsed */}
          {!isExpanded && (
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          )}
        </div>
      )}

      {/* Reference Resource Modal */}
      <ReferenceResourceModal
        modalState={modalState}
        onClose={handleCloseModal}
        onNavigateToReference={handleNavigateToReference}
        tenantId={tenantId}
      />
    </div>
  );
}

// Modal Component for Event Details
function EventDetailsModal({
  event,
  isOpen,
  onClose,
}: {
  event: TimelineEvent | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen || !event) return null;

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'user_created':
        return 'User Created';
      case 'user_synced':
        return 'User Synced';
      case 'visit':
        return 'Page Visit';
      case 'identification':
        return 'Form Submission';
      case 'phone_call':
        return 'Phone Call';
      case 'billable_event':
        return 'Revenue Event';
      case 'communication_email':
        return 'Email Sent';
      case 'communication_sms':
        return 'SMS Sent';
      default:
        return 'Event';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-medium text-gray-900 font-nav">
                  {getEventTypeLabel(event.type)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">{event.date.toLocaleString()}</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-4">
                {/* Summary */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Summary</h3>
                  <p className="text-sm text-gray-900">{event.description}</p>
                </div>

                {/* Raw Data */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Raw Data</h3>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-green-400 font-mono">
                      {JSON.stringify(event.data, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Timeline Component
function Timeline({ events }: { events: TimelineEvent[] }) {
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEventClick = (event: TimelineEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const sortedEvents = [...events].sort((a, b) => a.date.getTime() - b.date.getTime());

  const getEventColor = (type: string) => {
    switch (type) {
      case 'user_created':
        return 'bg-blue-500 border-blue-200';
      case 'user_synced':
        return 'bg-indigo-500 border-indigo-200';
      case 'visit':
        return 'bg-green-500 border-green-200';
      case 'identification':
        return 'bg-purple-500 border-purple-200';
      case 'phone_call':
        return 'bg-yellow-500 border-yellow-200';
      case 'billable_event':
        return 'bg-orange-500 border-orange-200';
      case 'communication_email':
        return 'bg-cyan-500 border-cyan-200';
      case 'communication_sms':
        return 'bg-pink-500 border-pink-200';
      default:
        return 'bg-gray-500 border-gray-200';
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'user_created':
        return '👤';
      case 'user_synced':
        return '🔄';
      case 'visit':
        return '👁️';
      case 'identification':
        return '📝';
      case 'phone_call':
        return '📞';
      case 'billable_event':
        return '💰';
      case 'communication_email':
        return '📧';
      case 'communication_sms':
        return '💬';
      default:
        return '⚫';
    }
  };

  if (sortedEvents.length === 0) {
    return <div className="text-center text-gray-500 py-8">No timeline events found</div>;
  }

  return (
    <div className="space-y-4">
      {/* Event Details Modal */}
      <EventDetailsModal event={selectedEvent} isOpen={isModalOpen} onClose={handleCloseModal} />

      {/* Timeline table */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                Event Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedEvents.map((event, index) => (
              <tr
                key={index}
                onClick={() => handleEventClick(event)}
                className={`hover:bg-gray-50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {event.date.toLocaleDateString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {event.date.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full border-2 ${getEventColor(event.type)} flex items-center justify-center mr-3 flex-shrink-0`}>
                      <span className="text-white text-sm">{getEventIcon(event.type)}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-900">{event.title}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 font-medium mb-1">{event.description}</div>
                  {event.type === 'visit' && (event.data as Visit).landingPageUrl && (
                    <div className="text-xs text-gray-500 break-all">
                      <span className="font-medium">URL:</span> {(event.data as Visit).landingPageUrl}
                    </div>
                  )}
                  {event.type === 'billable_event' && (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Event ID:</span> {(event.data as BillableEvent).id}
                      </div>
                      {(event.data as BillableEvent).external_id && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">External ID:</span>{' '}
                          {(event.data as BillableEvent).external_id}
                        </div>
                      )}
                    </div>
                  )}
                  {(event.type === 'user_created' || event.type === 'user_synced') && (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">User ID:</span> {(event.data as UserDetails).id}
                      </div>
                      {(event.data as UserDetails).externalId && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">External ID:</span>{' '}
                          {(event.data as UserDetails).externalId}
                        </div>
                      )}
                    </div>
                  )}
                  {(event.type === 'communication_email' || event.type === 'communication_sms') && (
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-1">
                        {(event.data as Communication).delivered && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20">
                            Delivered
                          </span>
                        )}
                        {(event.data as Communication).opened && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20">
                            Opened
                          </span>
                        )}
                        {(event.data as Communication).clicked && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20">
                            Clicked
                          </span>
                        )}
                        {(event.data as Communication).softBounce && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
                            Soft Bounce
                          </span>
                        )}
                        {(event.data as Communication).hardBounce && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20">
                            Hard Bounce
                          </span>
                        )}
                        {(event.data as Communication).spamComplaint && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20">
                            Spam
                          </span>
                        )}
                        {(event.data as Communication).unsubscribed && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20">
                            Unsubscribed
                          </span>
                        )}
                        {(event.data as Communication).error && (
                          <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20">
                            Error
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-xs text-gray-600">User Created</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
          <span className="text-xs text-gray-600">User Synced</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-xs text-gray-600">Visits</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500"></div>
          <span className="text-xs text-gray-600">Identifications</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <span className="text-xs text-gray-600">Phone Calls</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500"></div>
          <span className="text-xs text-gray-600">Billable Events</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
          <span className="text-xs text-gray-600">Emails</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-pink-500"></div>
          <span className="text-xs text-gray-600">SMS</span>
        </div>
      </div>
    </div>
  );
}

// The generic PhoneCall prefix is omitted because getPhoneCallSource extracts its UTM source below.
const PHONE_PREFIX_LABELS: Record<string, string> = {
  [IdentificationSourceIdPrefix.GoogleAdsPhoneCall]: 'Google Ads',
  [IdentificationSourceIdPrefix.GoogleLsaPhoneCall]: 'Google LSA',
  [IdentificationSourceIdPrefix.CallrailPhoneCall]: 'CallRail',
  [IdentificationSourceIdPrefix.CtmPhoneCall]: 'CTM',
  [IdentificationSourceIdPrefix.AlbiDniPhoneCall]: 'Albi DNI',
  [IdentificationSourceIdPrefix.VoiceAiCall]: 'Voice AI',
};
const LEGACY_SOURCE_ID_SEPARATOR = '|';

function isPhoneCallSource(sourceId: string | null | undefined): sourceId is string {
  if (!sourceId) return false;

  return PHONE_SOURCE_ID_PREFIXES_FOR_UTM.some(prefix =>
    sourceId.startsWith(prefix + IDENTIFICATION_SOURCE_ID_SEPARATOR),
  );
}

function getPhoneCallSource(sourceId: string): string {
  for (const [prefix, label] of Object.entries(PHONE_PREFIX_LABELS)) {
    if (sourceId.startsWith(prefix + IDENTIFICATION_SOURCE_ID_SEPARATOR)) return label;
  }

  // Generic "phone-call" prefix — extract source from after current or legacy separator.
  const sep = IDENTIFICATION_SOURCE_ID_SEPARATOR;
  if (sourceId.includes(sep)) {
    const utmSource = sourceId.split(sep)[1];
    if (utmSource && utmSource !== 'unknown') return utmSource;
  } else if (sourceId.includes(LEGACY_SOURCE_ID_SEPARATOR)) {
    const source = sourceId.split(LEGACY_SOURCE_ID_SEPARATOR)[1];
    if (source) return source;
  }

  return 'Phone';
}

function buildTimelineEvents(
  user: UserDetails,
  visits: Visit[],
  identifications: Identification[],
  billableEvents: BillableEvent[],
  communications: Communication[],
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // External user creation event (if available)
  if (user.externalCreatedDate) {
    events.push({
      date: new Date(user.externalCreatedDate),
      type: 'user_created',
      title: 'User Created (Partner)',
      description: 'Created in partner system',
      data: user,
    });
  }

  // Internal user creation/sync event
  if (user.externalCreatedDate) {
    events.push({
      date: new Date(user.createdAt),
      type: 'user_created',
      title: 'User Created',
      description: 'Account registered in our system',
      data: user,
    });

    events.push({
      date: new Date(user.updatedAt),
      type: 'user_synced',
      title: 'User Synced',
      description: 'Data synchronized from partner system',
      data: user,
    });
  } else {
    events.push({
      date: new Date(user.createdAt),
      type: 'user_created',
      title: 'User Created',
      description: 'Account registered',
      data: user,
    });
  }

  // Visit events
  visits.forEach(visit => {
    if (!visit.landingPageUrl) return;

    let description: string;
    try {
      const url = new URL(visit.landingPageUrl);
      description = url.pathname === '/' ? url.hostname : url.pathname;
    } catch {
      description =
        visit.landingPageUrl.length > 30
          ? `${visit.landingPageUrl.substring(0, 30)}...`
          : visit.landingPageUrl;
    }

    events.push({
      date: new Date(visit.createdAt),
      type: 'visit',
      title: 'Page Visit',
      description,
      data: visit,
    });
  });

  // Duplicate identifications with the same source, contact, and timestamp were observed in this internal debug view.
  const seenIdentifications = new Set<string>();
  identifications.forEach(identification => {
    const eventDate = identification.createdAt || identification.submitted_at;
    if (!eventDate) return;

    const dedupeKey = `${identification.sourceId ?? ''}|${identification.email ?? ''}|${identification.phone ?? ''}|${eventDate}`;
    if (seenIdentifications.has(dedupeKey)) return;
    seenIdentifications.add(dedupeKey);

    const sourceId = identification.sourceId;
    const isPhoneCall = isPhoneCallSource(sourceId);
    const contactDescription = identification.email
      ? `Email: ${identification.email}`
      : `Phone: ${identification.phone}`;

    const description = isPhoneCall ? `Source: ${getPhoneCallSource(sourceId)}` : contactDescription;

    events.push({
      date: new Date(eventDate),
      type: isPhoneCall ? 'phone_call' : 'identification',
      title: isPhoneCall ? 'Phone Call' : 'Form Submitted',
      description,
      data: identification,
    });
  });

  // Billable events
  billableEvents.forEach(event => {
    const amount = event.amount
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: event.currency || 'USD',
        }).format(parseFloat(event.amount))
      : '$0.00';

    events.push({
      date: new Date(event.created_date || event.paid_date || event.due_date || new Date()),
      type: 'billable_event',
      title: 'Revenue Event',
      description: amount,
      data: event,
    });
  });

  // Communications (emails and SMS)
  communications.forEach(communication => {
    const type =
      communication.communicationType === 'EMAIL' ? 'communication_email' : 'communication_sms';
    const title = communication.communicationType === 'EMAIL' ? 'Email Sent' : 'SMS Sent';

    // Build status description
    let statusParts = [];
    if (communication.delivered) statusParts.push('Delivered');
    if (communication.opened) statusParts.push('Opened');
    if (communication.clicked) statusParts.push('Clicked');
    if (communication.softBounce) statusParts.push('Soft Bounce');
    if (communication.hardBounce) statusParts.push('Hard Bounce');
    if (communication.spamComplaint) statusParts.push('Spam');
    if (communication.unsubscribed) statusParts.push('Unsubscribed');
    if (communication.error) statusParts.push('Error');

    const description = statusParts.length > 0 ? statusParts.join(', ') : 'Sent';

    events.push({
      date: new Date(communication.createdAt),
      type: type as TimelineEvent['type'],
      title,
      description,
      data: communication,
    });
  });

  return events;
}

// Tabs Component
function Tabs({ activeTab, setActiveTab }: { activeTab: string; setActiveTab: (tab: string) => void }) {
  const tabs = [
    { id: 'timeline', label: 'Timeline' },
    { id: 'details', label: 'Details' },
  ];

  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}>
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

export const UserDetailPage: React.FC = () => {
  const { externalId } = useParams<{ externalId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('timeline');

  // Get tenantId from URL parameters
  const tenantId = searchParams.get('tenantId');

  // Generic resource modal state
  const [selectedResource, setSelectedResource] = useState<{
    externalId: string;
    displayName: string;
    type: string;
  } | null>(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedResourceSchemaId, setSelectedResourceSchemaId] = useState<string>('');

  // Fetch user details with timeline data using tenant-scoped endpoint
  const {
    data: userDetailsData,
    isLoading: isLoadingUser,
    error,
  } = useQuery({
    queryKey: ['user-details', externalId, tenantId],
    queryFn: async () => {
      if (!externalId || !tenantId) {
        throw new Error('External ID and tenant ID are required');
      }
      return await partnerApi.getTenantPartnerUserDetails(externalId, tenantId);
    },
    enabled: !!externalId && !!tenantId,
  });

  // Fetch schema mappings to get location schemas
  const { data: schemaMappings } = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  // Get all schemas from mappings
  const allSchemas = [
    ...(Array.isArray(schemaMappings?.locationsSchema) ? schemaMappings.locationsSchema : []),
    ...(Array.isArray(schemaMappings?.transactionsSchema) ? schemaMappings.transactionsSchema : []),
    ...(Array.isArray(schemaMappings?.contactsSchema) ? schemaMappings.contactsSchema : []),
    ...(Array.isArray(schemaMappings?.customSchema) ? schemaMappings.customSchema : []),
  ];

  // Handle viewing any resource
  const handleViewResource = async (resourceId: string, resourceType: string, displayName?: string) => {
    setSelectedResource({
      externalId: resourceId,
      displayName: displayName || resourceId,
      type: resourceType,
    });
    setShowResourceModal(true);

    // Filter schemas by type
    const relevantSchemas = allSchemas.filter(schema => schema.type === resourceType);

    // If there are multiple schemas, try to find the one that works
    if (relevantSchemas.length > 1) {
      setSelectedResourceSchemaId(''); // Clear selection to trigger the search

      // Try each schema until one succeeds
      for (const schema of relevantSchemas) {
        try {
          await partnerApi.getPartnerResourceByExternalId(user.tenantId, resourceId, schema.schemaId);
          // If successful, set this schema and break
          setSelectedResourceSchemaId(schema.schemaId);
          break;
        } catch (error) {
          // Continue to next schema if this one fails
          console.log(`Schema ${schema.schemaId} failed for ${resourceType} ${resourceId}:`, error);
        }
      }
    } else if (relevantSchemas.length === 1) {
      // If there's only one schema, use it directly
      setSelectedResourceSchemaId(relevantSchemas[0].schemaId);
    }
  };

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !userDetailsData) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="bg-red-50 p-4 rounded-lg text-red-700">
          {error ? `Error loading user details: ${error.message}` : 'User not found'}
        </div>
      </div>
    );
  }

  const user = userDetailsData.data.user;
  const visits = userDetailsData.data.visits || [];
  const identifications = userDetailsData.data.identifications || [];
  const billableEvents = userDetailsData.data.billableEvents || [];
  const communications =
    'communications' in userDetailsData.data ? userDetailsData.data.communications : [];

  const fullName =
    user.firstName || user.lastName
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : 'Unnamed User';

  const timelineEvents = buildTimelineEvents(
    user,
    visits,
    identifications,
    billableEvents,
    communications,
  );

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-xl font-medium text-gray-900 font-nav">{fullName}</h1>
      </div>

      {/* User Info Card */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">User Information</h2>

        {/* Core user fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <div className="text-sm text-gray-500">External ID</div>
            <div className="font-mono text-sm mt-1">{user.externalId}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Email</div>
            <div className="text-sm mt-1">
              {user.email || <span className="text-gray-400">No email</span>}
            </div>
            {user.emailOptOut ? (
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20 mt-1">
                Opted Out
              </span>
            ) : user.email ? (
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 mt-1">
                Opted In
              </span>
            ) : null}
          </div>
          <div>
            <div className="text-sm text-gray-500">Phone</div>
            <div className="text-sm mt-1">
              {user.phone || <span className="text-gray-400">No phone</span>}
            </div>
            {user.phoneOptOut ? (
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20 mt-1">
                Opted Out
              </span>
            ) : user.phone ? (
              <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 mt-1">
                Opted In
              </span>
            ) : null}
          </div>
          <div>
            <div className="text-sm text-gray-500">Tenant</div>
            <div className="text-sm mt-1">{user.tenantName}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Status</div>
            <div className="mt-1">
              {user.status === 'deactivated' ? (
                <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20">
                  Deactivated
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20">
                  Active
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Created</div>
            <div className="text-sm mt-1">
              {new Date(user.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Last Updated</div>
            <div className="text-sm mt-1">
              {new Date(user.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </div>
          </div>
          {user.externalCreatedDate && (
            <div>
              <div className="text-sm text-gray-500">Created in Partner System</div>
              <div className="text-sm mt-1">
                {new Date(user.externalCreatedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </div>
            </div>
          )}
          {'billableEventsCount' in user &&
            user.billableEventsCount !== undefined &&
            user.billableEventsCount > 0 && (
              <div>
                <div className="text-sm text-gray-500">Revenue Events</div>
                <div className="text-sm mt-1">{user.billableEventsCount}</div>
              </div>
            )}
          {'totalRevenue' in user && user.totalRevenue !== undefined && user.totalRevenue > 0 && (
            <div>
              <div className="text-sm text-gray-500">Total Revenue</div>
              <div className="text-sm mt-1">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(user.totalRevenue)}
              </div>
            </div>
          )}
          {'subscriptionStatus' in user && user.subscriptionStatus && (
            <div>
              <div className="text-sm text-gray-500">Subscription Status</div>
              <div className="text-sm mt-1">{user.subscriptionStatus}</div>
            </div>
          )}
        </div>

        {/* Resource Relationships */}
        <ResourceRelationships
          externalId={user.externalId}
          tenantId={tenantId!}
          onResourceClick={handleViewResource}
        />

        {/* Custom Schema Fields */}
        {'resourceData' in user && user.resourceData && Object.keys(user.resourceData).length > 0 ? (
          <CustomSchemaFields data={user.resourceData} tenantId={user.tenantId} />
        ) : (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="text-sm text-gray-500 italic">
              No custom schema fields available for this user.
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Tab Content */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
        {activeTab === 'timeline' && (
          <div>
            <h2 className="text-lg font-semibold mb-6">User Timeline</h2>
            <Timeline events={timelineEvents} />
          </div>
        )}
        {activeTab === 'details' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Additional Details</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Internal User ID</div>
                <div className="font-mono text-sm mt-1">{user.id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Last Updated</div>
                <div className="text-sm mt-1">{new Date(user.updatedAt).toLocaleString()}</div>
              </div>
              {user.deactivatedAt && (
                <div>
                  <div className="text-sm text-gray-500">Deactivated At</div>
                  <div className="text-sm mt-1">{new Date(user.deactivatedAt).toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Generic Resource Modal */}
      {showResourceModal && selectedResource && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => {
              setShowResourceModal(false);
              setSelectedResource(null);
            }}
          />

          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-medium text-gray-900 font-nav">
                    {selectedResource.type
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}{' '}
                    Details
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedResource.type.charAt(0).toUpperCase() + selectedResource.type.slice(1)} ID:{' '}
                    {selectedResource.externalId}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowResourceModal(false);
                    setSelectedResource(null);
                  }}
                  className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              {selectedResourceSchemaId && (
                <div className="flex-1 overflow-y-auto">
                  <PartnerResourceModal
                    tenantId={user.tenantId}
                    resource={{
                      externalId: selectedResource.externalId,
                      displayName: selectedResource.displayName,
                    }}
                    isOpen={true}
                    onClose={() => {
                      setShowResourceModal(false);
                      setSelectedResource(null);
                    }}
                    schemaDefinitionId={selectedResourceSchemaId}
                  />
                </div>
              )}

              {/* Loading State */}
              {!selectedResourceSchemaId && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-gray-600">Loading resource details...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailPage;
